import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { CanvasRenderer } from '../render/CanvasRenderer';
import { useEditor } from '../state/store';
import { BRAILLE_TOOLS } from '../tools/registry';
import { Cell } from '../core/types';
import { buildVariableOverlay } from '../runtime/overlay';
import { VariableRegistry } from '../runtime/Variables';

/**
 * The editing surface.
 *
 * React owns the element and the event wiring; the renderer owns every pixel.
 * Redraws are scheduled on animation frames and driven by the store's revision
 * counter, so a drag never re-renders the component tree.
 */
export function Canvas(): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<CanvasRenderer | null>(null);
  const frameRef = useRef(0);
  const pointerDown = useRef(false);

  const revision = useEditor((s) => s.revision);
  const renderOptions = useEditor((s) => s.renderOptions);
  const preview = useEditor((s) => s.preview);
  const marquee = useEditor((s) => s.marquee);
  const cursor = useEditor((s) => s.cursor);
  const caret = useEditor((s) => s.caret);
  const tool = useEditor((s) => s.tool);
  const activeTabId = useEditor((s) => s.activeTabId);
  const showVariables = useEditor((s) => s.showVariablePreview);

  /**
   * Variable values are drawn as a ghost overlay rather than written into the
   * document, and the in-progress tool preview wins over them so a stroke is
   * never hidden behind a substituted value.
   */
  const overlay = useMemo(() => {
    const tab = useEditor.getState().active();
    if (!showVariables || tab.doc.variables.length === 0) return preview;
    const registry = new VariableRegistry(tab.doc.variables);
    const values = registry.resolveScope(tab.doc.previewValues, true);
    const vars = buildVariableOverlay(tab.doc.composite(), registry, values);
    if (vars.size === 0) return preview;
    if (!preview) return vars;
    const merged = new Map<number, Cell>(vars);
    for (const [k, v] of preview) merged.set(k, v);
    return merged;
  }, [revision, showVariables, preview, activeTabId]);

  const scheduleRender = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      rendererRef.current?.render();
    });
  }, []);

  useLayoutEffect(() => {
    if (!canvasRef.current) return;
    rendererRef.current = new CanvasRenderer(canvasRef.current);
    const host = hostRef.current;
    if (!host) return;

    const resize = () => {
      const r = rendererRef.current;
      if (!r || !host) return;
      r.resize(host.clientWidth, host.clientHeight);
      scheduleRender();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    return () => observer.disconnect();
  }, [scheduleRender]);

  // Document / options / overlays -> renderer.
  useEffect(() => {
    const r = rendererRef.current;
    if (!r) return;
    const state = useEditor.getState();
    const tab = state.active();
    r.setDocument(tab.doc);
    r.setOptions(renderOptions);
    r.setOverlay({
      selection: tab.selection,
      marquee,
      cursor,
      caret,
      preview: overlay,
      brailleCursor: BRAILLE_TOOLS.has(tool),
    });
    scheduleRender();
  }, [revision, renderOptions, overlay, marquee, cursor, caret, tool, activeTabId, scheduleRender]);

  const pointFrom = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = rendererRef.current;
    const canvas = canvasRef.current;
    if (!r || !canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const hit = r.hitTest(e.clientX - rect.left, e.clientY - rect.top);
    const doc = useEditor.getState().active().doc;
    return { ...hit, inside: hit.x >= 0 && hit.y >= 0 && hit.x < doc.width && hit.y < doc.height };
  }, []);

  /**
   * Drags are clamped to the document.
   *
   * Letting a drag run past the edge produced shapes and selection rectangles
   * with out-of-range coordinates, which the renderer then painted outside the
   * canvas area where nothing ever cleared them.
   */
  const clampToDoc = useCallback((hit: { x: number; y: number; fx: number; fy: number }) => {
    const doc = useEditor.getState().active().doc;
    return {
      x: Math.max(0, Math.min(doc.width - 1, hit.x)),
      y: Math.max(0, Math.min(doc.height - 1, hit.y)),
      fx: hit.fx,
      fy: hit.fy,
    };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const hit = pointFrom(e);
      if (!hit || !hit.inside) return;
      const state = useEditor.getState();

      e.currentTarget.setPointerCapture(e.pointerId);
      pointerDown.current = true;

      if (state.tool === 'text') {
        state.setCaret({ x: hit.x, y: hit.y });
        return;
      }
      state.beginStroke(
        { x: hit.x, y: hit.y },
        { x: hit.fx, y: hit.fy },
        { shift: e.shiftKey, alt: e.altKey, ctrl: e.ctrlKey || e.metaKey },
        e.button,
      );
    },
    [pointFrom],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const hit = pointFrom(e);
      if (!hit) return;
      const state = useEditor.getState();
      if (!pointerDown.current) {
        // Outside the document there is no cell to report, so the highlight
        // disappears instead of sticking to the edge.
        state.setCursor(hit.inside ? { x: hit.x, y: hit.y } : null);
        return;
      }
      const clamped = clampToDoc(hit);
      state.updateStroke(
        { x: clamped.x, y: clamped.y },
        { x: clamped.fx, y: clamped.fy },
        { shift: e.shiftKey, alt: e.altKey, ctrl: e.ctrlKey || e.metaKey },
      );
    },
    [pointFrom, clampToDoc],
  );

  const endStroke = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!pointerDown.current) return;
    pointerDown.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    useEditor.getState().endStroke();
  }, []);

  const onScroll = useCallback(() => {
    const host = hostRef.current;
    const r = rendererRef.current;
    if (!host || !r) return;
    r.setViewport({ scrollX: host.scrollLeft, scrollY: host.scrollTop });
    scheduleRender();
  }, [scheduleRender]);

  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const state = useEditor.getState();
      const zoom = Math.max(0.25, Math.min(6, state.renderOptions.zoom * (e.deltaY < 0 ? 1.12 : 0.89)));
      state.setRenderOptions({ zoom: Number(zoom.toFixed(3)) });
    },
    [],
  );

  const r = rendererRef.current;
  const size = r?.documentSize() ?? { width: 0, height: 0 };

  // Signals that a drag will move the selection rather than start a new one.
  const overSelection =
    tool === 'select' &&
    cursor !== null &&
    (() => {
      const tab = useEditor.getState().active();
      return !tab.selection.isEmpty && tab.selection.has(cursor.x, cursor.y);
    })();
  const cursorStyle =
    tool === 'move' || overSelection ? 'move' : tool === 'text' ? 'text' : 'crosshair';

  return (
    <div
      ref={hostRef}
      className="canvas-host"
      onScroll={onScroll}
      onWheel={onWheel}
      data-testid="canvas-host"
    >
      <div
        className="canvas-scroll-sizer"
        style={{ width: size.width, height: size.height }}
      />
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
        onContextMenu={(e) => e.preventDefault()}
        style={{ cursor: cursorStyle }}
      />
    </div>
  );
}
